import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightLeft, ExternalLink, Search } from "lucide-react";

import Meta from "../../components/Meta";
import { Button } from "../../components/ui/button";
import {
  computeMatchDiff,
  matchReports,
  monthIndex,
  recordLabel,
  type MatchDiff,
  type ProgrammeRecord,
  type RecordMatch,
} from "../../lib/activeTravel/programmeDiff";
import { getManualMatchesForComparison } from "../../lib/activeTravel/programmeManualMatches";
import { getProjectDescription, type ProjectDescription } from "../../lib/activeTravel/projectDescriptions";

import octoberReport from "../../data/activeTravel/edinburgh/reports/2025-10.json";
import februaryReport from "../../data/activeTravel/edinburgh/reports/2026-02-03.json";
import latestReport from "../../data/activeTravel/edinburgh/reports/2026-06.json";
import pausedReport from "../../data/activeTravel/edinburgh/reports/2026-06-paused.json";

type UiMatch = RecordMatch & { diff: MatchDiff };
type ProgrammeReportSnapshot = {
  id: string;
  asOfDate: string;
  label: string;
  pdfPath: string;
  records: ProgrammeRecord[];
};
type PausedProject = {
  project: string;
  phase: string | null;
  description: string;
};
type PausedProgrammeSnapshot = {
  id: string;
  label: string;
  sourcePdfPath: string;
  records: PausedProject[];
};
const octoberSnapshot = octoberReport as ProgrammeReportSnapshot;
const februarySnapshot = februaryReport as ProgrammeReportSnapshot;
const latestSnapshot = latestReport as ProgrammeReportSnapshot;
const pausedSnapshot = pausedReport as PausedProgrammeSnapshot;

type TabId = "slipped" | "changed" | "matched" | "addedRemoved" | "onHold";
type SortId = "startDesc" | "endDesc" | "startSoon" | "alpha";
type BaselineId = "february" | "october";

const LATER_BADGE_TONE =
  "border-[rgba(220,38,38,0.46)] bg-[rgba(220,38,38,0.12)] text-[color-mix(in_srgb,#dc2626_52%,var(--c-text-primary))]";
const EARLIER_BADGE_TONE =
  "border-[rgba(4,120,87,0.46)] bg-[rgba(4,120,87,0.12)] text-[color-mix(in_srgb,#047857_52%,var(--c-text-primary))]";
const NEUTRAL_BADGE_TONE =
  "border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-bg-rgb),0.38)] text-[var(--c-text-primary)]";

function formatDurationMonths(totalMonths: number): string {
  const abs = Math.abs(totalMonths);
  const years = Math.floor(abs / 12);
  const months = abs % 12;

  if (years > 0 && months > 0) return `${years}y ${months}m`;
  if (years > 0) return `${years}y`;
  return `${months}m`;
}

function formatAverageMonths(totalMonths: number): string {
  if (!Number.isFinite(totalMonths) || totalMonths <= 0) return "0m";

  const rounded = Math.round(totalMonths * 10) / 10;
  if (rounded >= 10 || Number.isInteger(rounded)) return `${Math.round(rounded)}m`;

  return `${rounded.toFixed(1)}m`;
}

function formatMoved(deltaMonths: number): { short: string; title: string } {
  if (deltaMonths === 0) {
    return { short: "No change", title: "No change" };
  }

  const abs = Math.abs(deltaMonths);
  const duration = formatDurationMonths(abs);
  const monthsWord = abs === 1 ? "month" : "months";
  const direction = deltaMonths > 0 ? "later" : "earlier";

  return {
    short: `${deltaMonths > 0 ? "Later" : "Earlier"} ${duration}`,
    title: `Moved ${direction} by ${abs} ${monthsWord} (${duration})`,
  };
}

function formatMonthToken(token: string): string {
  const m = /^([A-Za-z]{3})-(\d{2})$/.exec(token.trim());
  if (!m) return token;

  const mon = m[1];
  const yy = Number(m[2]);
  if (Number.isNaN(yy)) return token;

  return `${mon} ${2000 + yy}`;
}

function monthIndexFromIsoDate(isoDate: string): number {
  // Ignore day component; we only need month-level granularity.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((isoDate ?? "").trim());
  if (!m) throw new Error(`Invalid ISO date: ${isoDate}`);

  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  if (Number.isNaN(year) || Number.isNaN(month) || month < 0 || month > 11) {
    throw new Error(`Invalid ISO date: ${isoDate}`);
  }

  return year * 12 + month;
}

function DeltaBadge({
  scope,
  deltaMonths,
  emphasize,
  prefersReducedMotion,
}: {
  scope?: string;
  deltaMonths: number;
  emphasize?: boolean;
  prefersReducedMotion: boolean;
}) {
  const tone =
    deltaMonths > 0
      ? "slip"
      : deltaMonths < 0
        ? "forward"
        : "neutral";

  const moved = formatMoved(deltaMonths);
  const statusLabel =
    deltaMonths > 0 ? "Delayed" : deltaMonths < 0 ? "Earlier" : "No change";
  const duration =
    deltaMonths === 0 ? null : formatDurationMonths(Math.abs(deltaMonths));

  const base =
    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold tracking-wide";

  const toneClass =
    tone === "slip"
      ? LATER_BADGE_TONE
      : tone === "forward"
        ? EARLIER_BADGE_TONE
        : NEUTRAL_BADGE_TONE;

  const pulse =
    emphasize && tone === "slip" && !prefersReducedMotion
      ? "shadow-[0_0_0_1px_rgba(239,68,68,0.10),0_0_25px_rgba(239,68,68,0.18)]"
      : "";

  return (
    <span className={`${base} ${toneClass} ${pulse}`} title={moved.title}>
      {scope ? (
        <span className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-90">
          {scope}
        </span>
      ) : null}
      <span className="text-[10px] uppercase tracking-[0.22em] opacity-95">
        {statusLabel}
      </span>
      {duration ? <span>{duration}</span> : null}
    </span>
  );
}

function DatePill({ start, end }: { start: string; end: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-2 rounded-md border border-[rgba(var(--c-border-rgb),0.55)] bg-[rgba(var(--c-surface-rgb),0.72)] px-2 py-1 text-xs text-[var(--c-text-primary)]">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
          Start
        </span>
        <span className="font-mono">{formatMonthToken(start)}</span>
      </span>
      <span className="text-[var(--c-text-disabled)]">to</span>
      <span className="inline-flex items-center gap-2 rounded-md border border-[rgba(var(--c-border-rgb),0.55)] bg-[rgba(var(--c-surface-rgb),0.72)] px-2 py-1 text-xs text-[var(--c-text-primary)]">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
          End
        </span>
        <span className="font-mono">{formatMonthToken(end)}</span>
      </span>
    </div>
  );
}

function StatusText({ status }: { status: string }) {
  return (
    <p className="mt-2 line-clamp-2 text-xs text-[var(--c-text-secondary)]">
      {status}
    </p>
  );
}

function KpiCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(var(--c-border-rgb),0.7)] bg-[rgba(var(--c-surface-rgb),0.82)] p-5 backdrop-blur-xl">
      <div className="absolute inset-0 opacity-40">
        <div
          className="absolute -top-24 -right-24 h-56 w-56 rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(211,175,55,0.18) 0%, transparent 60%)",
          }}
        />
      </div>
      <div className="relative">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
          {label}
        </p>
        <p className="mt-2 text-3xl font-bold tracking-tight text-[var(--c-text-primary)]">
          {value}
        </p>
        {hint ? (
          <p className="mt-1 text-xs text-[var(--c-text-secondary)]">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}

function byProjectAlpha(a: UiMatch, b: UiMatch): number {
  const al = uiRecordLabel(a.newRecord).toLowerCase();
  const bl = uiRecordLabel(b.newRecord).toLowerCase();
  return al.localeCompare(bl);
}

function compareStartSoonestFromNow(
  nowIndex: number,
  a: UiMatch,
  b: UiMatch,
): number {
  const aStart = monthIndex(a.newRecord.start);
  const bStart = monthIndex(b.newRecord.start);
  const aDelta = aStart - nowIndex;
  const bDelta = bStart - nowIndex;

  const aFuture = aDelta >= 0;
  const bFuture = bDelta >= 0;

  // Put future (upcoming) starts first, then already-started projects.
  if (aFuture !== bFuture) return aFuture ? -1 : 1;

  if (aFuture && bFuture) {
    if (aDelta !== bDelta) return aDelta - bDelta; // soonest start first
  } else {
    if (aDelta !== bDelta) return bDelta - aDelta; // closest past start first
  }

  // Stable tie-breakers.
  if (aStart !== bStart) return aStart - bStart;
  return byProjectAlpha(a, b);
}

function recordSearchText(rec: ProgrammeRecord): string {
  // Include both raw label and UI label so searches work for either.
  return `${recordLabel(rec)} ${uiRecordLabel(rec)}`.toLowerCase();
}

function uiProjectName(project: string, _phase: string | null): string {
  // Clarify this programme line item as the overall scheme (with sub-sections as phases).
  if (project === "Cameron Toll to BioQuarter") {
    return "Cameron Toll to BioQuarter general scheme";
  }

  return project;
}

function uiPhaseName(project: string, phase: string | null): string | null {
  if (!phase) return null;

  if (project === "Cameron Toll to BioQuarter" && phase === "Lady Road") {
    return "Lady Road section";
  }

  return phase;
}

function uiRecordLabel(rec: ProgrammeRecord): string {
  const project = uiProjectName(rec.project, rec.phase);
  const phase = uiPhaseName(rec.project, rec.phase);
  // Avoid non-ASCII separators here (keeps source + terminals happy on Windows).
  return phase ? `${project} - ${phase}` : project;
}

function PhasePill({ text }: { text: string }) {
  return (
    <div className="mt-2 inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border border-[rgba(var(--c-border-rgb),0.55)] bg-[rgba(var(--c-bg-rgb),0.22)] px-3 py-1">
      <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
        Phase
      </span>
      <span className="min-w-0 truncate text-xs font-semibold text-[var(--c-text-primary)]">
        {text}
      </span>
    </div>
  );
}

function ProjectExplanation({ description }: { description: ProjectDescription | null }) {
  if (!description) return null;

  return (
    <details className="group mt-3 border-l border-[rgba(var(--c-border-rgb),0.75)] pl-3">
      <summary className="cursor-pointer list-none text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--c-text-disabled)] transition-colors hover:text-[var(--c-text-primary)]">
        Project explanation
      </summary>
      <div className="mt-2 space-y-2 text-xs leading-relaxed text-[var(--c-text-secondary)]">
        <p>{description.summary}</p>
        {description.note ? (
          <p className="text-[var(--c-text-disabled)]">{description.note}</p>
        ) : null}
        <a
          href={description.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[var(--c-accent)] hover:underline"
        >
          Source: {description.sourceTitle}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </details>
  );
}

export default function ActiveTravelScheduleDiffPage() {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const [baselineId, setBaselineId] = useState<BaselineId>("february");
  const baselineSnapshot =
    baselineId === "october" ? octoberSnapshot : februarySnapshot;
  const referenceNowIndex = useMemo(
    () => monthIndexFromIsoDate(latestSnapshot.asOfDate),
    [],
  );

  const diff = useMemo(() => {
    const result = matchReports(
      baselineSnapshot.records,
      latestSnapshot.records,
      {
        manualMatches: getManualMatchesForComparison(
          baselineSnapshot.id,
          latestSnapshot.id,
        ),
      },
    );
    const matches: UiMatch[] = result.matches.map((m) => ({
      ...m,
      diff: computeMatchDiff(m),
    }));
    return { ...result, matches };
  }, [baselineSnapshot]);

  const [tab, setTab] = useState<TabId>("matched");
  const [sort, setSort] = useState<SortId>("startSoon");
  const [query, setQuery] = useState("");
  const [showEndDateChange, setShowEndDateChange] = useState(false);

  const stats = useMemo(() => {
    const startSlips = diff.matches.filter((m) => m.diff.deltaStartMonths > 0);
    const endSlips = diff.matches.filter((m) => m.diff.deltaEndMonths > 0);
    const totalPositiveStartDelay = diff.matches.reduce(
      (sum, m) => sum + Math.max(0, m.diff.deltaStartMonths),
      0,
    );
    const largestStartSlip =
      startSlips.length === 0
        ? null
        : startSlips.reduce((best, cur) =>
            cur.diff.deltaStartMonths > best.diff.deltaStartMonths ? cur : best
          );

    return {
      startSlipCount: startSlips.length,
      endSlipCount: endSlips.length,
      averageStartDelay:
        diff.matches.length === 0 ? 0 : totalPositiveStartDelay / diff.matches.length,
      largestStartSlip,
    };
  }, [diff]);

  const filteredMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = diff.matches.filter((m) => {
      if (!q) return true;
      return (
        recordSearchText(m.oldRecord).includes(q) ||
        recordSearchText(m.newRecord).includes(q)
      );
    });

    const tabFiltered =
      tab === "slipped"
        ? matches.filter((m) => m.diff.deltaStartMonths > 0)
        : tab === "changed"
          ? matches.filter(
              (m) =>
                m.diff.deltaStartMonths !== 0 ||
                m.diff.deltaEndMonths !== 0 ||
                m.diff.statusChanged
            )
          : tab === "matched"
            ? matches
            : [];

    const sorted = [...tabFiltered].sort((a, b) => {
      if (sort === "alpha") return byProjectAlpha(a, b);
      if (sort === "startSoon") {
        return compareStartSoonestFromNow(referenceNowIndex, a, b);
      }
      if (sort === "endDesc") {
        if (b.diff.deltaEndMonths !== a.diff.deltaEndMonths) {
          return b.diff.deltaEndMonths - a.diff.deltaEndMonths;
        }
        if (b.diff.deltaStartMonths !== a.diff.deltaStartMonths) {
          return b.diff.deltaStartMonths - a.diff.deltaStartMonths;
        }
        return byProjectAlpha(a, b);
      }

      // startDesc (default)
      if (b.diff.deltaStartMonths !== a.diff.deltaStartMonths) {
        return b.diff.deltaStartMonths - a.diff.deltaStartMonths;
      }
      if (b.diff.deltaEndMonths !== a.diff.deltaEndMonths) {
        return b.diff.deltaEndMonths - a.diff.deltaEndMonths;
      }
      return byProjectAlpha(a, b);
    });

    return sorted;
  }, [diff.matches, query, referenceNowIndex, sort, tab]);

  const filteredAddedRemoved = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filter = (r: ProgrammeRecord) => !q || recordSearchText(r).includes(q);

    return {
      added: diff.added.filter(filter).sort((a, b) =>
        uiRecordLabel(a).toLowerCase().localeCompare(uiRecordLabel(b).toLowerCase())
      ),
      removed: diff.removed.filter(filter).sort((a, b) =>
        uiRecordLabel(a).toLowerCase().localeCompare(uiRecordLabel(b).toLowerCase())
      ),
    };
  }, [diff.added, diff.removed, query]);

  const filteredOnHoldProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pausedSnapshot.records.filter((project) => {
      if (!q) return true;
      return `${project.project} ${project.phase ?? ""} ${project.description}`
        .toLowerCase()
        .includes(q);
    });
  }, [query]);

  const tabs: Array<{ id: TabId; label: string; hint?: string }> = [
    { id: "slipped", label: "Delayed starts", hint: "Start moved later" },
    { id: "changed", label: "Any change", hint: "Dates or status" },
    { id: "matched", label: "All projects" },
    { id: "addedRemoved", label: "New / missing" },
    { id: "onHold", label: "On hold", hint: `${pausedSnapshot.records.length} in June PDF` },
  ];

  const sortOptions: Array<{ id: SortId; label: string }> = [
    { id: "startSoon", label: "Soonest starts - from now" },
    { id: "startDesc", label: "Most delayed starts" },
    { id: "endDesc", label: "Most delayed ends" },
    { id: "alpha", label: "Project A-Z" },
  ];

  const baselineOptions: Array<{ id: BaselineId; label: string }> = [
    { id: "february", label: februarySnapshot.label },
    { id: "october", label: octoberSnapshot.label },
  ];

  return (
    <>
      <Meta
        title="Active Travel Programme Timeline Changes | Edinburgh | City Scope"
        description="A friendly diff view of Edinburgh Council active travel programme timelines against the June 2026 update. Instantly see which projects moved later."
        canonical="/active-travel"
      />

      <div className="relative w-full min-h-screen overflow-x-hidden bg-[var(--c-bg)] text-[var(--c-text-primary)]">
        {/* Background atmosphere */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 neo-civic-grid opacity-25" />
          <div
            className="absolute -top-48 left-1/2 h-[820px] w-[820px] -translate-x-1/2 rounded-full blur-[120px]"
            style={{
              background:
                "radial-gradient(circle, rgba(211,175,55,0.16) 0%, transparent 60%)",
            }}
          />
          <div
            className="absolute top-40 -left-24 h-[520px] w-[520px] rounded-full blur-[120px]"
            style={{
              background:
                "radial-gradient(circle, rgba(96,165,250,0.10) 0%, transparent 65%)",
            }}
          />
          <div
            className="absolute top-64 -right-32 h-[520px] w-[520px] rounded-full blur-[120px]"
            style={{
              background:
                "radial-gradient(circle, rgba(248,113,113,0.08) 0%, transparent 65%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 60% at 50% 35%, transparent 0%, rgba(var(--c-bg-rgb), 0.55) 100%)",
            }}
          />
        </div>

        <div className="relative z-10">
          {/* Hero */}
          <section className="px-4 sm:px-6 lg:px-8 pt-[calc(var(--navbar-height,64px)+1.5rem)] pb-10">
            <div className="mx-auto max-w-6xl">
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-3">
                  <div className="inline-flex items-center gap-2 self-start rounded-full border border-[rgba(var(--c-border-rgb),0.8)] bg-[rgba(var(--c-surface-rgb),0.65)] px-4 py-2 backdrop-blur-xl">
                    <ArrowRightLeft className="h-4 w-4 text-[var(--c-accent)]" />
                    <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-secondary)]">
                      Timeline changes
                    </span>
                  </div>

                  <h1 className="font-['Space_Grotesk'] text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[0.95]">
                    Active Travel Programme{" "}
                    <span className="text-[var(--c-accent)]">Timeline Changes</span>
                  </h1>

                  <p className="max-w-3xl text-base sm:text-lg text-[var(--c-text-secondary)] leading-relaxed">
                    Edinburgh Council:{" "}
                    <span className="text-[var(--c-text-primary)]">{baselineSnapshot.label}</span>{" "}
                    vs{" "}
                    <span className="text-[var(--c-text-primary)]">{latestSnapshot.label}</span>. We flag projects where the{" "}
                    <span className="text-[var(--c-text-primary)]">construction start date moved later</span>.
                  </p>

                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
                      Compare from
                    </span>
                    <div className="inline-flex rounded-xl border border-[rgba(var(--c-border-rgb),0.7)] bg-[rgba(var(--c-bg-rgb),0.28)] p-1">
                      {baselineOptions.map((option) => {
                        const active = option.id === baselineId;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setBaselineId(option.id)}
                            className={[
                              "h-9 rounded-lg px-3 text-xs font-semibold transition-colors",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]/40",
                              active
                                ? "bg-[rgba(var(--c-accent-rgb),0.18)] text-[var(--c-text-primary)]"
                                : "text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]",
                            ].join(" ")}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-2 flex flex-col sm:flex-row gap-3">
                    <Button asChild className="rounded-xl">
                      <a
                        href={latestSnapshot.pdfPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2"
                      >
                        Open {latestSnapshot.label} PDF <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button asChild variant="secondary" className="rounded-xl">
                      <a
                        href={baselineSnapshot.pdfPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2"
                      >
                        Open {baselineSnapshot.label} PDF <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>

                {/* KPI grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <KpiCard
                    label="Starts delayed"
                    value={`${stats.startSlipCount}`}
                    hint="Construction start moved later"
                  />
                  <KpiCard
                    label="Biggest delay"
                    value={
                      stats.largestStartSlip
                        ? formatDurationMonths(stats.largestStartSlip.diff.deltaStartMonths)
                        : "0m"
                    }
                    hint={
                      stats.largestStartSlip
                        ? uiRecordLabel(stats.largestStartSlip.newRecord)
                        : "No delayed starts detected"
                    }
                  />
                  <KpiCard
                    label="Ends delayed"
                    value={`${stats.endSlipCount}`}
                    hint="Construction end moved later"
                  />
                  <KpiCard
                    label="Average delay"
                    value={formatAverageMonths(stats.averageStartDelay)}
                    hint={`Positive start delays averaged across ${diff.matches.length} matched projects`}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Controls */}
          <section className="px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <div
                className="sticky z-30 rounded-2xl border border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-surface-rgb),0.78)] backdrop-blur-xl px-4 py-4"
                style={{ top: "var(--navbar-height, 0px)" }}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  {/* Tabs */}
                  <div className="flex flex-wrap items-center gap-2">
                    {tabs.map((t) => {
                      const active = t.id === tab;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setTab(t.id)}
                          className={[
                            "group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold",
                            "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]/40",
                            active
                              ? "border-[rgba(var(--c-accent-rgb),0.55)] bg-[rgba(var(--c-accent-rgb),0.14)] text-[var(--c-text-primary)]"
                              : "border-[rgba(var(--c-border-rgb),0.55)] bg-[rgba(var(--c-surface-rgb),0.55)] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] hover:border-[rgba(var(--c-accent-rgb),0.25)]",
                          ].join(" ")}
                        >
                          <span>{t.label}</span>
                          {t.hint ? (
                            <span className="hidden sm:inline font-mono text-[10px] opacity-70">{t.hint}</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  {/* Search + sort */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--c-text-disabled)]" />
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by project name"
                        className={[
                          "h-10 w-full sm:w-[320px] rounded-xl border px-10 text-sm",
                          "border-[rgba(var(--c-border-rgb),0.7)] bg-[rgba(var(--c-bg-rgb),0.35)]",
                          "text-[var(--c-text-primary)] placeholder:text-[var(--c-text-disabled)]",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]/40",
                        ].join(" ")}
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
                        Sort
                      </span>
                      <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value as SortId)}
                        className={[
                          "h-10 rounded-xl border px-3 text-sm",
                          "border-[rgba(var(--c-border-rgb),0.7)] bg-[rgba(var(--c-bg-rgb),0.35)]",
                          "text-[var(--c-text-primary)]",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]/40",
                        ].join(" ")}
                      >
                        {sortOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>

                      <Button
                        type="button"
                        variant={showEndDateChange ? "secondary" : "outline"}
                        className="rounded-xl"
                        aria-pressed={showEndDateChange}
                        onClick={() => setShowEndDateChange((v) => !v)}
                      >
                        {showEndDateChange ? "Hide end date" : "Show end date"}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--c-text-disabled)]">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] ${LATER_BADGE_TONE}`}>
                      Red = later
                    </span>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] ${EARLIER_BADGE_TONE}`}>
                      Green = earlier
                    </span>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] ${NEUTRAL_BADGE_TONE}`}>
                      Grey = no change
                    </span>
                    <span>Dates are month-year from the council PDFs.</span>
                  </div>

                  <details className="rounded-xl border border-[rgba(var(--c-border-rgb),0.55)] bg-[rgba(var(--c-bg-rgb),0.25)] px-3 py-2">
                    <summary className="cursor-pointer list-none text-xs font-semibold text-[var(--c-text-primary)]">
                      About this comparison
                    </summary>
                    <div className="mt-2 space-y-2 text-xs text-[var(--c-text-secondary)]">
                      <p>
                        Projects are matched by name and phase. If a project was renamed between snapshots, it may show
                        up as new or missing.
                      </p>
                      <p>
                        If something looks surprising, use the Open PDF buttons above to double-check the source.
                      </p>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </section>

          {/* Results */}
          <section className="px-4 sm:px-6 lg:px-8 pt-6 pb-24">
            <div className="mx-auto max-w-6xl">
              {tab === "addedRemoved" ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <details className="rounded-2xl border border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-surface-rgb),0.72)] backdrop-blur-xl p-5">
                    <summary className="cursor-pointer list-none">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-[var(--c-text-primary)]">
                            New in {latestSnapshot.label}
                          </p>
                          <p className="text-xs text-[var(--c-text-secondary)]">
                            {filteredAddedRemoved.added.length} record(s)
                          </p>
                        </div>
                        <span className="font-mono text-xs text-[var(--c-text-disabled)]">expand</span>
                      </div>
                    </summary>
                    <div className="mt-4 space-y-3">
                      {filteredAddedRemoved.added.length === 0 ? (
                        <p className="text-sm text-[var(--c-text-secondary)]">No added records match your search.</p>
                      ) : (
                        filteredAddedRemoved.added.map((r) => (
                          <div
                            key={`${r.project}||${r.phase ?? ""}||${r.start}||${r.end}`}
                            className="rounded-xl border border-[rgba(var(--c-border-rgb),0.6)] bg-[rgba(var(--c-bg-rgb),0.22)] p-4"
                          >
                            <p className="text-sm font-semibold text-[var(--c-text-primary)]">
                              {uiRecordLabel(r)}
                            </p>
                            <ProjectExplanation description={getProjectDescription(r.project)} />
                            <div className="mt-2">
                              <DatePill start={r.start} end={r.end} />
                              <StatusText status={r.status} />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </details>

                  <details className="rounded-2xl border border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-surface-rgb),0.72)] backdrop-blur-xl p-5">
                    <summary className="cursor-pointer list-none">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-[var(--c-text-primary)]">
                            No longer listed (since {baselineSnapshot.label})
                          </p>
                          <p className="text-xs text-[var(--c-text-secondary)]">
                            {filteredAddedRemoved.removed.length} record(s)
                          </p>
                        </div>
                        <span className="font-mono text-xs text-[var(--c-text-disabled)]">expand</span>
                      </div>
                    </summary>
                    <div className="mt-4 space-y-3">
                      {filteredAddedRemoved.removed.length === 0 ? (
                        <p className="text-sm text-[var(--c-text-secondary)]">No removed records match your search.</p>
                      ) : (
                        filteredAddedRemoved.removed.map((r) => (
                          <div
                            key={`${r.project}||${r.phase ?? ""}||${r.start}||${r.end}`}
                            className="rounded-xl border border-[rgba(var(--c-border-rgb),0.6)] bg-[rgba(var(--c-bg-rgb),0.22)] p-4"
                          >
                            <p className="text-sm font-semibold text-[var(--c-text-primary)]">
                              {uiRecordLabel(r)}
                            </p>
                            <ProjectExplanation description={getProjectDescription(r.project)} />
                            <div className="mt-2">
                              <DatePill start={r.start} end={r.end} />
                              <StatusText status={r.status} />
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </details>
                </div>
              ) : tab === "onHold" ? (
                <div className="space-y-3">
                  <div className="rounded-2xl border border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-surface-rgb),0.72)] p-4 backdrop-blur-xl">
                    <p className="text-sm font-semibold text-[var(--c-text-primary)]">
                      Currently paused projects
                    </p>
                    <p className="mt-1 max-w-3xl text-xs leading-relaxed text-[var(--c-text-secondary)]">
                      The June PDF opens with a currently paused projects table. These rows do
                      not include start or end months, so they sit outside the dated comparison.
                    </p>
                    <a
                      href={pausedSnapshot.sourcePdfPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--c-accent)] hover:underline"
                    >
                      Open June PDF
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>

                  {filteredOnHoldProjects.length === 0 ? (
                    <div className="rounded-2xl border border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-surface-rgb),0.72)] p-8 text-center text-[var(--c-text-secondary)]">
                      No on-hold projects match your search.
                    </div>
                  ) : (
                    filteredOnHoldProjects.map((project) => (
                      <div
                        key={`${project.project}||${project.phase ?? ""}`}
                        className="rounded-2xl border border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-surface-rgb),0.72)] p-4 backdrop-blur-xl"
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[var(--c-text-primary)]">
                              {project.project}
                            </p>
                            {project.phase ? <PhasePill text={project.phase} /> : null}
                          </div>
                          <span className={`inline-flex shrink-0 items-center self-start rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-[0.14em] ${NEUTRAL_BADGE_TONE}`}>
                            Paused
                          </span>
                        </div>

                        <p className="mt-3 text-xs leading-relaxed text-[var(--c-text-secondary)]">
                          {project.description}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Desktop header */}
                  <div className="hidden md:grid grid-cols-[minmax(240px,2.2fr)_1.2fr_1.2fr_0.9fr] gap-4 px-4 py-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
                    <div>Project</div>
                    <div>{baselineSnapshot.label}</div>
                    <div>{latestSnapshot.label}</div>
                    <div>{showEndDateChange ? "Change" : "Start change"}</div>
                  </div>

                  {filteredMatches.length === 0 ? (
                    <div className="rounded-2xl border border-[rgba(var(--c-border-rgb),0.75)] bg-[rgba(var(--c-surface-rgb),0.72)] p-8 text-center text-[var(--c-text-secondary)]">
                      No matches for this filter/search.
                    </div>
                  ) : (
                    filteredMatches.map((m, idx) => {
                      const slipped = m.diff.deltaStartMonths > 0;
                      const changed =
                        m.diff.deltaStartMonths !== 0 ||
                        m.diff.deltaEndMonths !== 0 ||
                        m.diff.statusChanged;

                      const rowKey = `${m.oldRecord.project}||${m.oldRecord.phase ?? ""}||${m.newRecord.start}||${m.newRecord.end}`;
                      const projectDescription = getProjectDescription(m.newRecord.project);

                      return (
                        <motion.div
                          key={rowKey}
                          initial={
                            prefersReducedMotion
                              ? undefined
                              : { opacity: 0, y: 10 }
                          }
                          animate={
                            prefersReducedMotion
                              ? undefined
                              : { opacity: 1, y: 0 }
                          }
                          transition={
                            prefersReducedMotion
                              ? undefined
                              : {
                                  duration: 0.25,
                                  delay: Math.min(idx * 0.02, 0.35),
                                }
                          }
                          className={[
                            "rounded-2xl border backdrop-blur-xl",
                            "bg-[rgba(var(--c-surface-rgb),0.72)]",
                            slipped
                              ? "border-red-500/25 shadow-[0_10px_30px_rgba(0,0,0,0.18)]"
                              : "border-[rgba(var(--c-border-rgb),0.75)]",
                          ].join(" ")}
                        >
                          {/* Desktop row */}
                          <div className="hidden md:grid grid-cols-[minmax(240px,2.2fr)_1.2fr_1.2fr_0.9fr] gap-4 p-4">
                            <div className="min-w-0">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-[var(--c-text-primary)] truncate">
                                    {uiProjectName(m.newRecord.project, m.newRecord.phase)}
                                  </p>
                                  {uiPhaseName(m.newRecord.project, m.newRecord.phase) ? (
                                    <PhasePill
                                      text={uiPhaseName(
                                        m.newRecord.project,
                                        m.newRecord.phase,
                                      )!}
                                    />
                                  ) : null}
                                  <ProjectExplanation description={projectDescription} />
                                </div>

                                <div className="flex items-center gap-2">
                                  {slipped ? (
                                    <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.14em] ${LATER_BADGE_TONE}`}>
                                      delayed start
                                    </span>
                                  ) : null}
                                  {changed && !slipped ? (
                                    <span className="inline-flex items-center rounded-full border border-[rgba(var(--c-accent-rgb),0.35)] bg-[rgba(var(--c-accent-rgb),0.10)] px-2 py-1 text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-primary)]">
                                      updated
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </div>

                            <div>
                              <DatePill start={m.oldRecord.start} end={m.oldRecord.end} />
                              <StatusText status={m.oldRecord.status} />
                            </div>

                            <div>
                              <DatePill start={m.newRecord.start} end={m.newRecord.end} />
                              <StatusText status={m.newRecord.status} />
                            </div>

                            <div className="flex flex-col gap-2">
                              <DeltaBadge
                                scope={showEndDateChange ? "Start" : undefined}
                                deltaMonths={m.diff.deltaStartMonths}
                                emphasize
                                prefersReducedMotion={prefersReducedMotion}
                              />
                              {showEndDateChange ? (
                                <DeltaBadge
                                  scope="End"
                                  deltaMonths={m.diff.deltaEndMonths}
                                  prefersReducedMotion={prefersReducedMotion}
                                />
                              ) : null}
                            </div>
                          </div>

                          {/* Mobile card */}
                          <div className="md:hidden p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-base font-semibold text-[var(--c-text-primary)]">
                                  {uiProjectName(m.newRecord.project, m.newRecord.phase)}
                                </p>
                                {uiPhaseName(m.newRecord.project, m.newRecord.phase) ? (
                                  <PhasePill
                                    text={uiPhaseName(
                                      m.newRecord.project,
                                      m.newRecord.phase,
                                    )!}
                                  />
                                ) : null}
                                <ProjectExplanation description={projectDescription} />
                              </div>
                              {slipped ? (
                                <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.14em] ${LATER_BADGE_TONE}`}>
                                  delayed start
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3">
                              <div className="rounded-xl border border-[rgba(var(--c-border-rgb),0.6)] bg-[rgba(var(--c-bg-rgb),0.22)] p-3">
                                <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
                                  {baselineSnapshot.label}
                                </p>
                                <div className="mt-2">
                                  <DatePill start={m.oldRecord.start} end={m.oldRecord.end} />
                                  <StatusText status={m.oldRecord.status} />
                                </div>
                              </div>

                              <div className="rounded-xl border border-[rgba(var(--c-border-rgb),0.6)] bg-[rgba(var(--c-bg-rgb),0.22)] p-3">
                                <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--c-text-disabled)]">
                                  {latestSnapshot.label}
                                </p>
                                <div className="mt-2">
                                  <DatePill start={m.newRecord.start} end={m.newRecord.end} />
                                  <StatusText status={m.newRecord.status} />
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                <DeltaBadge
                                  scope={showEndDateChange ? "Start" : undefined}
                                  deltaMonths={m.diff.deltaStartMonths}
                                  emphasize
                                  prefersReducedMotion={prefersReducedMotion}
                                />
                                {showEndDateChange ? (
                                  <DeltaBadge
                                    scope="End"
                                    deltaMonths={m.diff.deltaEndMonths}
                                    prefersReducedMotion={prefersReducedMotion}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
